import { seededRandom, shade } from '../pixel.js';
import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, drawCylinder, faceQuad, faces, frontFace, iso, seatWithBack } from './iso.js';
import type { Face, FurniDrawer } from './iso.js';

/**
 * Workplace and commerce furniture (job stations, shop fixtures, mall and
 * office decor). Owned by the economy feature. Stations show their "working"
 * side on the face the item is rotated towards.
 */

/** Face a front-facing detail goes on; items turned away show it on the left. */
const front = (rotation: number): Face => frontFace(rotation) ?? 'left';

/** Along which axis a long, thin item lies for a rotation. */
const alongV = (rotation: number) => rotation === 2 || rotation === 6;

/* -------------------------------------------------------------------------- */
/* Job stations                                                               */
/* -------------------------------------------------------------------------- */

function drawBaristaStation(pc: PixelCanvas, rotation: number): void {
  const face = front(rotation);
  drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 28, faces('#2f8f83')));
  faceQuad(pc, face, 0.46, -0.4, 0.4, 10, 18, '#f0bf36');
  faceQuad(pc, face, 0.46, -0.3, 0.3, 12, 16, '#6b4226');
  drawBox(pc, box(-0.48, 0.48, -0.48, 0.48, 28, 31, faces('#b98a5c', '#d9ae7e')));
  // Espresso machine at the back, chrome with a red stripe.
  drawBox(pc, box(-0.35, 0.05, -0.35, 0.05, 31, 52, faces('#b8bec4', '#d6dade')));
  faceQuad(pc, 'left', 0.05, -0.35, 0.05, 44, 47, '#d24039');
  faceQuad(pc, 'right', 0.05, -0.35, 0.05, 44, 47, '#a8322c');
  faceQuad(pc, 'left', 0.05, -0.22, -0.08, 34, 40, '#2f2f38');
  // Milo tin and a stack of cups.
  drawCylinder(pc, 0.09, 31, 43, '#6aa84f', '#2f8f3a', 0.26, 0.2);
  drawCylinder(pc, 0.07, 31, 38, '#f7f3ea', '#dcd5c6', 0.3, -0.18);
  drawCylinder(pc, 0.07, 38, 42, '#f7f3ea', '#dcd5c6', 0.3, -0.18);
  // Tea sock hanging from a pole.
  const pole = iso(-0.05, 0.28, 31);
  pc.rect(pole.x, pole.y - 18, 1, 18, '#5a5a62');
  pc.ellipse(pole.x + 2, pole.y - 8, 3, 5, '#e8dcc4');
}

function drawOfficeDesk(pc: PixelCanvas, rotation: number): void {
  const wood = faces('#b98a5c', '#d4a877');
  const legs = faces('#6f757c');
  const [uh, vh] = alongV(rotation) ? [0.35, 0.46] : [0.46, 0.35];
  drawBoxes(pc, [
    box(-uh, -uh + 0.06, -vh, -vh + 0.06, 0, 22, legs),
    box(uh - 0.06, uh, -vh, -vh + 0.06, 0, 22, legs),
    box(uh - 0.06, uh, vh - 0.06, vh, 0, 22, legs),
    box(-uh, -uh + 0.06, vh - 0.06, vh, 0, 22, legs),
  ]);
  drawBox(pc, box(-uh, uh, -vh, vh, 22, 25, wood));

  // Monitor at the back of the desk, facing the worker's side.
  const dark = faces('#2a2a33', '#3a3a44');
  const screen = '#7fb7d9';
  switch (rotation) {
    case 2:
      drawBox(pc, box(-0.25, -0.19, -0.25, 0.25, 27, 44, dark));
      faceQuad(pc, 'right', -0.19, -0.21, 0.21, 30, 42, screen);
      drawBox(pc, box(-0.05, 0.15, -0.2, 0.2, 25, 26, faces('#e8e4da')));
      break;
    case 4:
      drawBox(pc, box(-0.25, 0.25, -0.25, -0.19, 27, 44, dark));
      faceQuad(pc, 'left', -0.19, -0.21, 0.21, 30, 42, screen);
      drawBox(pc, box(-0.2, 0.2, -0.05, 0.15, 25, 26, faces('#e8e4da')));
      break;
    case 6:
      drawBox(pc, box(0.19, 0.25, -0.25, 0.25, 27, 44, dark));
      break;
    default:
      drawBox(pc, box(-0.25, 0.25, 0.19, 0.25, 27, 44, dark));
  }
  // A mug of kopi.
  drawCylinder(pc, 0.05, 25, 30, '#6b4428', '#f7f3ea', 0.3 * (rotation === 6 ? -1 : 1), 0.22);
}

function drawCashRegister(pc: PixelCanvas, rotation: number): void {
  const face = front(rotation);
  drawBox(pc, box(-0.42, 0.42, -0.42, 0.42, 0, 26, faces('#efeae0', '#fffaf0')));
  faceQuad(pc, face, 0.42, -0.42, 0.42, 16, 20, '#d9433b');
  drawBox(pc, box(-0.22, 0.22, -0.18, 0.18, 26, 34, faces('#3a3a44', '#4a4a52')));
  drawBox(pc, box(-0.15, 0.15, -0.1, 0.1, 34, 41, faces('#2a2a33')));
  faceQuad(pc, face, face === 'left' ? 0.1 : 0.15, -0.12, 0.12, 36, 40, '#6dff8a');
  // Keypad dots.
  const pad = iso(0.05, 0.05, 34);
  for (let i = 0; i < 3; i += 1) pc.rect(pad.x - 4 + i * 3, pad.y - 1, 2, 1, '#f0bf36');
  // Receipt roll.
  drawCylinder(pc, 0.05, 34, 37, '#fffaf0', '#dcd5c6', -0.28, 0.2);
}

function drawHawkerStall(pc: PixelCanvas, rotation: number): void {
  const face = front(rotation);
  drawBox(pc, box(-0.48, 0.48, -0.48, 0.48, 0, 30, faces('#a9b0b7', '#c7cdd2')));
  faceQuad(pc, face, 0.48, -0.44, 0.44, 6, 24, '#d24039');
  faceQuad(pc, face, 0.48, -0.36, 0.36, 12, 18, '#f0bf36');
  drawBox(pc, box(-0.5, 0.5, -0.5, 0.5, 30, 32, faces('#8b9299', '#d6dade')));
  // Wok and a steamer basket.
  const wok = iso(-0.12, 0.1, 32);
  pc.ellipse(wok.x, wok.y - 1, 9, 4, '#2a2a33');
  pc.ellipse(wok.x, wok.y - 2, 6, 2, '#c9a27a');
  drawCylinder(pc, 0.12, 32, 40, '#d4b483', '#b58a5c', 0.22, -0.18);
  // Menu board on poles along the back edge.
  const back: [number, number] = alongV(rotation) ? [-0.48, -0.42] : [-0.48, -0.42];
  const pole = faces('#5a5a62');
  if (alongV(rotation)) {
    drawBox(pc, box(back[0], back[1], -0.48, -0.42, 32, 62, pole));
    drawBox(pc, box(back[0], back[1], 0.42, 0.48, 32, 62, pole));
    drawBox(pc, box(back[0], back[1], -0.5, 0.5, 62, 80, faces('#f0bf36', '#ffd966')));
    faceQuad(pc, 'right', back[1], -0.42, 0.42, 66, 69, '#d24039');
    faceQuad(pc, 'right', back[1], -0.42, 0.1, 72, 75, '#6b4226');
  } else {
    drawBox(pc, box(-0.48, -0.42, back[0], back[1], 32, 62, pole));
    drawBox(pc, box(0.42, 0.48, back[0], back[1], 32, 62, pole));
    drawBox(pc, box(-0.5, 0.5, back[0], back[1], 62, 80, faces('#f0bf36', '#ffd966')));
    faceQuad(pc, 'left', back[1], -0.42, 0.42, 66, 69, '#d24039');
    faceQuad(pc, 'left', back[1], -0.42, 0.1, 72, 75, '#6b4226');
  }
}

/* -------------------------------------------------------------------------- */
/* Shops and malls                                                            */
/* -------------------------------------------------------------------------- */

const GOODS = ['#d9433b', '#3a6fd6', '#3d9e4c', '#f0bf36', '#8a52c8', '#ef7d2c', '#f19bb7', '#2c9f9b'];

function drawShopCounter(pc: PixelCanvas, rotation: number): void {
  const face = front(rotation);
  drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 10, faces('#8a5a34', '#a8703f')));
  drawBox(pc, box(-0.44, 0.44, -0.44, 0.44, 10, 28, faces('#cfe8f2', '#e6f4fa')));
  const rand = seededRandom(31 + rotation);
  for (let s = -0.36; s < 0.34; s += 0.14) {
    faceQuad(pc, face, 0.44, s, s + 0.09, 13, 13 + 4 + Math.floor(rand() * 6), GOODS[Math.floor(rand() * GOODS.length)]!);
  }
  drawBox(pc, box(-0.48, 0.48, -0.48, 0.48, 28, 31, faces('#8a5a34', '#b17a48')));
  // A little bell.
  const bell = iso(0.15, 0.1, 31);
  pc.ellipse(bell.x, bell.y - 3, 3, 3, '#f0bf36');
  pc.px(bell.x, bell.y - 7, '#6b4226');
}

function drawMallDirectory(pc: PixelCanvas, rotation: number): void {
  const face = front(rotation);
  const board = faces('#2b3a8f', '#3a4cb0');
  drawBox(pc, box(-0.26, 0.26, -0.26, 0.26, 0, 6, faces('#8b9299')));
  const [u0, u1, v0, v1] = alongV(rotation) ? [-0.08, 0.08, -0.32, 0.32] : [-0.32, 0.32, -0.08, 0.08];
  drawBox(pc, box(u0, u1, v0, v1, 6, 62, board));
  drawBox(pc, box(u0 - 0.02, u1 + 0.02, v0 - 0.02, v1 + 0.02, 62, 66, faces('#f0bf36', '#ffd966')));
  const at = face === 'left' ? v1 : u1;
  // A little map of shop blocks, and the "i" for information.
  faceQuad(pc, face, at, -0.26, 0.26, 16, 50, '#e8e4da');
  const blocks: Array<[number, number, number, number, string]> = [
    [-0.22, -0.06, 38, 48, '#d9433b'],
    [0.0, 0.22, 38, 48, '#3d9e4c'],
    [-0.22, -0.06, 24, 34, '#f0bf36'],
    [0.0, 0.22, 24, 34, '#8a52c8'],
  ];
  for (const [s0, s1, h0, h1, color] of blocks) faceQuad(pc, face, at, s0, s1, h0, h1, color);
  faceQuad(pc, face, at, -0.04, 0.03, 18, 21, '#3a4cb0');
  faceQuad(pc, face, at, -0.04, 0.03, 53, 59, '#fffaf0');
}

function drawShopShelf(pc: PixelCanvas, rotation: number): void {
  const face = frontFace(rotation);
  const wood = faces('#c9a27a', '#e0bf94');
  const piece = alongV(rotation) ? box(-0.2, 0.2, -0.46, 0.46, 0, 56, wood) : box(-0.46, 0.46, -0.2, 0.2, 0, 56, wood);
  drawBox(pc, piece);
  if (!face) return;
  faceQuad(pc, face, 0.2, -0.42, 0.42, 3, 52, '#8a6a4a');
  const rand = seededRandom(rotation + 57);
  for (const base of [4, 20, 36]) {
    faceQuad(pc, face, 0.2, -0.44, 0.44, base - 2, base, '#e0bf94');
    let s = -0.4;
    while (s < 0.36) {
      const width = 0.07 + rand() * 0.05;
      faceQuad(pc, face, 0.2, s, s + width, base, base + 7 + Math.floor(rand() * 5), GOODS[Math.floor(rand() * GOODS.length)]!);
      s += width + 0.02;
    }
  }
}

function drawClothesRack(pc: PixelCanvas, rotation: number): void {
  const metal = faces('#8b9299', '#b8bec4');
  const v = alongV(rotation);
  const pole = (s: number) => (v ? box(-0.03, 0.03, s - 0.03, s + 0.03, 0, 48, metal) : box(s - 0.03, s + 0.03, -0.03, 0.03, 0, 48, metal));
  drawBox(pc, pole(-0.4));
  // Hanging clothes along the bar, drawn on its near face.
  const rand = seededRandom(rotation + 13);
  const face: Face = v ? 'right' : 'left';
  for (let s = -0.34; s < 0.32; s += 0.11) {
    const color = GOODS[Math.floor(rand() * GOODS.length)]!;
    faceQuad(pc, face, 0.03, s, s + 0.09, 18 + Math.floor(rand() * 6), 44, color);
    faceQuad(pc, face, 0.03, s + 0.07, s + 0.09, 22, 44, shade(color, 0.75));
  }
  drawBox(pc, pole(0.4));
  drawBox(pc, v ? box(-0.03, 0.03, -0.42, 0.42, 46, 49, metal) : box(-0.42, 0.42, -0.03, 0.03, 46, 49, metal));
}

function drawMallBench(pc: PixelCanvas, rotation: number): void {
  const wood = faces('#b98a5c', '#d4a877');
  const leg = faces('#5a5a62');
  const [uh, vh] = alongV(rotation) ? [0.2, 0.46] : [0.46, 0.2];
  drawBoxes(pc, [
    box(-uh, -uh + 0.06, -vh, -vh + 0.06, 0, 12, leg),
    box(uh - 0.06, uh, -vh, -vh + 0.06, 0, 12, leg),
    box(uh - 0.06, uh, vh - 0.06, vh, 0, 12, leg),
    box(-uh, -uh + 0.06, vh - 0.06, vh, 0, 12, leg),
  ]);
  drawBox(pc, box(-uh, uh, -vh, vh, 12, 15, wood));
}

function drawSaleSign(pc: PixelCanvas, rotation: number): void {
  const v = alongV(rotation);
  const leg = faces('#8a5a34');
  drawBoxes(pc, v ? [box(-0.04, 0.04, -0.3, -0.24, 0, 12, leg), box(-0.04, 0.04, 0.24, 0.3, 0, 12, leg)] : [box(-0.3, -0.24, -0.04, 0.04, 0, 12, leg), box(0.24, 0.3, -0.04, 0.04, 0, 12, leg)]);
  drawBox(pc, v ? box(-0.04, 0.04, -0.32, 0.32, 12, 48, faces('#d9433b')) : box(-0.32, 0.32, -0.04, 0.04, 12, 48, faces('#d9433b')));
  const face: Face = v ? 'right' : 'left';
  // "SALE" in chunky white bars, then a yellow "%".
  const letters: Array<[number, number, number, number]> = [
    [-0.26, -0.14, 36, 44],
    [-0.1, 0.02, 36, 44],
    [0.06, 0.1, 36, 44],
    [0.14, 0.26, 36, 44],
  ];
  for (const [s0, s1, h0, h1] of letters) faceQuad(pc, face, 0.04, s0, s1, h0, h1, '#fffaf0');
  faceQuad(pc, face, 0.04, -0.12, 0.12, 18, 30, '#f0bf36');
}

/* -------------------------------------------------------------------------- */
/* Office decor                                                               */
/* -------------------------------------------------------------------------- */

function drawOfficeChair(pc: PixelCanvas, rotation: number): void {
  drawCylinder(pc, 0.2, 0, 2, '#3a3a44', '#2a2a33');
  drawCylinder(pc, 0.04, 2, 13, '#8b9299', '#6f757c');
  drawBoxes(pc, seatWithBack(rotation, 0.24, [13, 17], 38, 0.07, faces('#3a3a44', '#4a4a56'), faces('#2a2a33', '#3a3a44')));
}

function drawWaterCooler(pc: PixelCanvas): void {
  drawBox(pc, box(-0.2, 0.2, -0.2, 0.2, 0, 30, faces('#e8eef2', '#f7fafc')));
  faceQuad(pc, 'left', 0.2, -0.1, -0.02, 18, 22, '#3a6fd6');
  faceQuad(pc, 'left', 0.2, 0.02, 0.1, 18, 22, '#d9433b');
  drawCylinder(pc, 0.16, 30, 52, '#bfe6fb', '#9fd3ef');
  const shine = iso(-0.08, 0.05, 44);
  pc.rect(shine.x - 4, shine.y, 1, 8, '#e6f6ff');
  drawCylinder(pc, 0.06, 52, 55, '#3a6fd6', '#2a55a8');
}

function drawFilingCabinet(pc: PixelCanvas, rotation: number): void {
  const face = frontFace(rotation) ?? 'left';
  drawBox(pc, box(-0.25, 0.25, -0.28, 0.28, 0, 46, faces('#8b949c', '#a9b0b7')));
  const at = face === 'left' ? 0.28 : 0.25;
  for (let i = 0; i < 4; i += 1) {
    const h = 3 + i * 11;
    faceQuad(pc, face, at, -0.22, 0.22, h + 9, h + 10, '#5a6168');
    faceQuad(pc, face, at, -0.05, 0.05, h + 5, h + 7, '#d6dade');
  }
}

export const WORK_DRAWERS: Record<string, FurniDrawer> = {
  barista_station: drawBaristaStation,
  office_desk: drawOfficeDesk,
  cash_register: drawCashRegister,
  hawker_stall: drawHawkerStall,
  shop_counter: drawShopCounter,
  mall_directory: drawMallDirectory,
  shop_shelf: drawShopShelf,
  clothes_rack: drawClothesRack,
  mall_bench: drawMallBench,
  sale_sign: drawSaleSign,
  office_chair: drawOfficeChair,
  water_cooler: drawWaterCooler,
  filing_cabinet: drawFilingCabinet,
};
